import { Card, Flex, Grid, Text } from '@servicetitan/anvil2';
import { DataItem, formatCurrency } from '@servicetitan/financing-utils-shared';
import { DecisionDataStore } from '../stores/DecisionDataStore';
import {
    PrequalificationResponseStore,
    useProviderConfig,
} from '@servicetitan/financing-workflow-shared';

export interface SoftAppDetailsProps {
    hideDetails?: boolean;
}

export const SoftAppDetails = ({ hideDetails = false }: SoftAppDetailsProps) => {
    const { decisionData } = DecisionDataStore.useStore();
    const { prequalificationResponse } = PrequalificationResponseStore.useStore();
    const { termMultiplier } = useProviderConfig();
    const { approvedAmount, term, rate, description } = decisionData ?? {};
    const date = new Date().toLocaleDateString();

    /*
     * Some "prequals" are actual regular applications with a special BUID
     * (such as GoodLeap). In those cases we have an actual loanId but show
     * it like it's a "prequal".
     *
     * We use truthy checks (not nullish coalescing) because empty strings
     * should also fall back to externalLoanId.
     */
    const externalPrequalId = prequalificationResponse?.externalPrequalId;
    const hasPrequalId = !!externalPrequalId;
    const applicationId = hasPrequalId ? externalPrequalId : decisionData?.externalLoanId;
    const applicationIdLabel = hasPrequalId ? 'Pre-Qualification ID' : 'Application ID';

    return (
        <Card flexDirection="column" style={{ maxWidth: '845px', width: '100%' }}>
            <Flex gap="3" direction="column">
                <Text variant="headline" el="h2" size="small">
                    Details
                </Text>
                <Grid rowGap="4" templateColumns="repeat(auto-fit, minmax(255px, 1fr))">
                    <DataItem label={applicationIdLabel} text={applicationId} />
                    <DataItem label="Date" text={date} />
                    {!hideDetails && (
                        <>
                            <DataItem
                                label="Loan Amount"
                                text={formatCurrency(approvedAmount ?? 0)}
                            />
                            <DataItem
                                label="Available Credit"
                                text={formatCurrency(approvedAmount ?? 0)}
                            />

                            <DataItem
                                label="Term"
                                text={
                                    term ? [term * termMultiplier, 'months'].join(' ') : undefined
                                }
                            />
                            <DataItem label="APR" text={rate?.toFixed(2) + '%'} />
                            <DataItem
                                label="Plan Description"
                                text={description ? <Text>{description}</Text> : undefined}
                            />
                        </>
                    )}
                </Grid>
            </Flex>
        </Card>
    );
};
